import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { CollectionsNav } from '../components/CollectionsNav';
import { InstallAppControl } from '../components/InstallApp';
import {
  NAV_GLYPHS,
  NAV_SECTIONS,
  NAV_START,
  OPERATOR_SECTION,
  sectionContainsActive,
  sectionCount,
  teamSection,
  type NavCtx,
} from './navConfig';
import { NavRow, NavSection } from './NavSection';
import { AccountAccessRow } from './AccountAccess';
import { IdeaRail } from '../ideaScope/IdeaScopeNav';
import { ideaIdFromPath } from '../ideaScope/surfaces';

const SEEN_KEY = 'ed.nav.seen';

/** Last rail mode shown, so the idea/global crossfade plays only when the mode changes. */
let lastMode: 'idea' | 'global' | null = null;

/**
 * The desktop/tablet rail: an always-visible start group, collapsible sections
 * (every category name fits above the fold), and Install/Settings pinned
 * outside the scroll area. When the list still overflows (short laptops), a
 * "↓ N more sections" pill and an edge fade say so; both vanish at the end.
 */
export function SidebarNav({
  ctx,
  isAdmin,
  roles = [],
  onNavigate,
}: {
  ctx: NavCtx;
  isAdmin: boolean;
  roles?: string[];
  onNavigate: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(0);
  const [pulse, setPulse] = useState(false);

  const team = teamSection(roles);
  // Team tools sit first, right under the start group, so team members reach
  // their timesheet / board in one click.
  const sections = [...(team ? [team] : []), ...NAV_SECTIONS, ...(isAdmin ? [OPERATOR_SECTION] : [])];

  // Count section headers below the visible area (and whether we're at the end).
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    if (atEnd) {
      setHidden(0);
      return;
    }
    const bottom = el.getBoundingClientRect().bottom - 24;
    let n = 0;
    el.querySelectorAll<HTMLElement>('.nsec__hd').forEach((h) => {
      if (h.getBoundingClientRect().top > bottom) n++;
    });
    // Overflowing but every header visible (an open section runs long): still cue.
    setHidden(Math.max(n, 0) || -1);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, ctx.location]);

  useEffect(() => {
    const el = scrollRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    // Size changes cover section toggles (mid-transition too) and window resizes.
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    ro.observe(inner);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [measure]);

  // First visit only: one gentle pulse on the cue so it's noticed.
  useEffect(() => {
    if (hidden === 0) return;
    try {
      if (localStorage.getItem(SEEN_KEY)) return;
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      return;
    }
    const t = setTimeout(() => setPulse(true), 600);
    return () => clearTimeout(t);
  }, [hidden]);

  // Inside an idea, the middle of the rail becomes that idea's steps; the
  // global sections fold under "More in EntreDash" (closed on every visit).
  const ideaId = ideaIdFromPath(ctx.location);
  const [globalOpen, setGlobalOpen] = useState(false);
  useEffect(() => setGlobalOpen(false), [ideaId]);

  // "Pick another step" from a Next band: draw the eye to the rail.
  useEffect(() => {
    const onOpen = () => {
      const rail = scrollRef.current?.querySelector<HTMLElement>('.isc-rail');
      if (!rail) return;
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      rail.classList.remove('is-flash');
      void rail.offsetWidth;
      rail.classList.add('is-flash');
      // Focus the suggested step (or the first one) so the keyboard lands there too.
      const target = rail.querySelector<HTMLElement>('.isc-next')?.closest<HTMLElement>('.isc-row') ?? rail.querySelector<HTMLElement>('.isc-row');
      target?.focus({ preventScroll: true });
    };
    window.addEventListener('ed:open-idea-map', onOpen);
    return () => window.removeEventListener('ed:open-idea-map', onOpen);
  }, []);

  const globalSections = (
    <>
      {sections.map((s) => (
        <Fragment key={s.id}>
          <NavSection
            id={s.id}
            label={s.label}
            hint={s.hint}
            count={sectionCount(s)}
            containsActive={sectionContainsActive(s, ctx)}
          >
            {s.items.map((item) => (
              <NavRow key={item.key} item={item} ctx={ctx} onNavigate={onNavigate} />
            ))}
          </NavSection>
          {/* Collections (pinned tags) sits next to the idea-bearing
              surfaces it filters. Renders nothing without pins. */}
          {s.id === 'intelligence' && <CollectionsNav onNavigate={onNavigate} />}
        </Fragment>
      ))}
    </>
  );

  const more = hidden !== 0;
  // Inside an idea the step list is short: keep the edge fade, skip the floating pill
  // (it would sit on top of the last steps).
  const showPill = more && (!ideaId || globalOpen);
  const moreLabel = hidden > 0 ? `${hidden} more section${hidden === 1 ? '' : 's'}` : 'More below';

  return (
    <>
      {/* Start group: Dashboard / Spark / Capture stay put, outside the
          scrolling list, so they're always one click away. */}
      <div className="sidebar__start">
        {NAV_START.map((item) => (
          <NavRow key={item.key} item={item} ctx={ctx} onNavigate={onNavigate} />
        ))}
        <div className="sidebar__rule" />
      </div>
      <div className="sidebar__list">
      <div className={`sidebar__scroll${more ? ' has-more' : ''}`} ref={scrollRef}>
        <div ref={innerRef}>
          {ideaId ? (
            <>
              <ModeIn key="idea" mode="idea" cls="isc-rail-in">
                <IdeaRail ideaId={ideaId} location={ctx.location} />
              </ModeIn>
              <button
                type="button"
                className="isc-more"
                aria-expanded={globalOpen}
                onClick={() => setGlobalOpen((v) => !v)}
              >
                <span>More in EntreDash</span>
                <i aria-hidden="true" />
              </button>
              {globalOpen && globalSections}
            </>
          ) : (
            <ModeIn key="global" mode="global" cls="sidebar__global-in">
              {globalSections}
            </ModeIn>
          )}
        </div>
      </div>

      <button
        type="button"
        className={`sidebar__more${showPill ? ' is-shown' : ''}${pulse ? ' is-pulse' : ''}`}
        tabIndex={showPill ? 0 : -1}
        aria-hidden={!showPill}
        onClick={() => {
          const el = scrollRef.current;
          el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }}
        onAnimationEnd={() => setPulse(false)}
      >
        <span aria-hidden="true">↓</span> {moreLabel}
      </button>
      </div>

      <div className="sidebar__bottom">
        <AccountAccessRow onNavigate={onNavigate} compact />
        <InstallAppControl className="sidebar__item sidebar__install" onActivate={onNavigate}>
          <span className="sidebar__item-num" aria-hidden="true">
            {NAV_GLYPHS.install}
          </span>
          <span className="sidebar__item-label">Install app</span>
        </InstallAppControl>
        <button
          type="button"
          className="sidebar__item"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate?.();
            window.dispatchEvent(new Event('ed:open-feedback'));
          }}
        >
          <span className="sidebar__item-num" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="sidebar__item-label">Feedback</span>
        </button>
        <Link
          href="/settings"
          className={`sidebar__item${ctx.location.startsWith('/settings') ? ' is-active' : ''}`}
          onClick={onNavigate}
        >
          <span className="sidebar__item-num" aria-hidden="true">
            {NAV_GLYPHS.gear}
          </span>
          <span className="sidebar__item-label">Settings</span>
        </Link>
      </div>
    </>
  );
}

/** Plays the rail's slide-in only when the rail switches between idea and global mode. */
function ModeIn({ mode, cls, children }: { mode: 'idea' | 'global'; cls: string; children: React.ReactNode }) {
  const [play] = useState(() => lastMode !== null && lastMode !== mode);
  useEffect(() => {
    lastMode = mode;
  }, [mode]);
  return <div className={play ? cls : undefined}>{children}</div>;
}
